import React, { useMemo } from 'react';
import { applyGlobalStyles } from '../global-styles';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Snackbar, Alert } from '@mui/material';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';
import { useSlidePanelAnimation } from '../../../../hooks/useSlidePanelAnimation';
import { CloseConfirmationModal } from '../../../../components/modals/confirmation';
import { ConfirmationModal as GlobalConfirmationModal } from '../../../ui/Modal';

import ProgressBar from '../ProgressBar/index';
import StepIndicators from '../StepIndicators/index';
import StepHeader from '../StepHeader/index';
import ProductSelectionStep from '../ProductSelectionStep/index';
import ConfirmationStep from '../ConfirmationStep/index';
import NavigationFooter from '../NavigationFooter/index';
import ConfirmationModal from '../ConfirmationModal/index';
import ProductPropertiesStep from '../ProductPropertiesStep/ProductPropertiesStep';

import { useAddInventoryPanel } from './AddInventoryPanel.hooks';
import {
  PanelOverlay,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelLoadingBody,
  LoadingContent,
  LoadingSpinner,
  LoadingText,
  StepContent,
} from './AddInventoryPanel.styles';

interface AddInventoryPanelProps {
  open: boolean;
  onClose: () => void;
  onSave?: (data: any) => void | Promise<void>;
  editMode?: boolean;
  recordToEdit?: any;
}

/**
 * Note: What we call "branches" in the frontend are "locations" in the backend.
 * The relationship between locations and products is stored in "branches_inventory_products"
 * (or locations_inventory_products from the backend perspective).
 */

const AddInventoryPanel: React.FC<AddInventoryPanelProps> = ({
  open,
  onClose,
  onSave,
  editMode = false,
  recordToEdit = null,
}) => {
  const {
    // State
    locationsData,
    locationsLoading,
    productsData,
    productsLoading,
    currentStep,
    productItems,
    selectedLocationId,
    selectedSupplierId,
    showSupplierValidation,
    handleSupplierChange,
    showConfirmModal,
    confirmMessage,
    showSaveDraftConfirmModal,
    snackbarOpen,
    snackbarMessage,
    setSnackbarOpen,
    productUnits,
    isSubmitting,
    isSubmittingWithItems,
    isConfirming,
    isSavingDraft,

    // Actions
    addProductRow,
    removeProductRow,
    updateProductItem,
    handleNextStep,
    handlePreviousStep,
    validateStep1,
    validateStep2,
    handleUpdateProperties,
    handleUpdateItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    startConfirmation,
    executeConfirmation,
    resetConfirmation,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleLocationChange,
    handleOpenSaveDraftConfirm,
    handleConfirmSaveDraft,
    handleCancelSaveDraftConfirm,

    // Close confirmation
    showCancelConfirmation,
    setShowCancelConfirmation,
    handleCloseClick,
    cancelOperation,

    showReductionWarningModal,
    handleConfirmReductionWarning,
    handleCancelReductionWarning,
  } = useAddInventoryPanel({ open, onClose, onSave, editMode, recordToEdit });

  const isBusy = isSubmitting || isSubmittingWithItems || isConfirming || isSavingDraft;

  const supplierEditPin = useMemo((): { id: number; name: string } | null => {
    if (!editMode || !recordToEdit) return null;
    const id = recordToEdit.supplier?.id ?? recordToEdit.supplier_id;
    const name = recordToEdit.supplier?.name;
    if (id == null || name == null || String(name).trim() === '') return null;
    return { id: Number(id), name: String(name) };
  }, [editMode, recordToEdit]);

  // Aplicar estilos globales
  React.useEffect(() => {
    applyGlobalStyles();
  }, []);

  const { shouldRender, panelClassName } = useSlidePanelAnimation(open);

  // Cerrar con ESC (debe estar antes del early return)
  useEscapeKey(
    () => {
      if (
        open &&
        !isBusy &&
        !showCancelConfirmation &&
        !showSaveDraftConfirmModal &&
        !showReductionWarningModal &&
        !showConfirmModal
      ) {
        handleCloseClick();
      }
    },
    open &&
      !isBusy &&
      !showCancelConfirmation &&
      !showSaveDraftConfirmModal &&
      !showReductionWarningModal &&
      !showConfirmModal,
  );

  // Solo renderizar mientras anima abrir/cerrar (mismo shell que catálogo)
  if (!shouldRender) {
    return null;
  }

  const isDataLoading = productsLoading || locationsLoading;

  // Handler para cerrar al hacer click en el overlay
  const handleOverlayClick = (e: React.MouseEvent) => {
    // Solo cerrar si se hace click directamente en el overlay, no en el panel
    if (e.target === e.currentTarget && !isBusy) {
      handleCloseClick();
    }
  };

  return (
    <>
      <PanelOverlay className={panelClassName} onClick={handleOverlayClick} />
      <PanelContainer className={panelClassName}>
        {/* Panel Header */}
        <PanelHeader>
          <PanelTitle>{editMode ? 'Editar' : 'Ingreso de'} Inventario</PanelTitle>
          <CloseButton onClick={handleCloseClick} aria-label="Cerrar panel" disabled={isBusy}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CloseButton>
        </PanelHeader>

        {isDataLoading ? (
          <PanelLoadingBody>
            <LoadingContent>
              <LoadingSpinner />
              <LoadingText>Cargando datos...</LoadingText>
            </LoadingContent>
          </PanelLoadingBody>
        ) : (
          <>
            <ProgressBar currentStep={currentStep} totalSteps={3} />

            <StepIndicators currentStep={currentStep} />

            <StepHeader currentStep={currentStep} />

            {/* Step Content */}
            <StepContent>
              {currentStep === 1 && (
                <ProductSelectionStep
                  productItems={productItems}
                  products={productsData}
                  supplierCatalogIsActive={open && currentStep === 1}
                  supplierEditPin={supplierEditPin}
                  selectedSupplierId={selectedSupplierId}
                  showSupplierError={showSupplierValidation}
                  onSupplierChange={handleSupplierChange}
                  onAddRow={addProductRow}
                  onRemoveRow={removeProductRow}
                  onUpdateItem={updateProductItem}
                />
              )}

              {/* New Product Properties Step */}
              {currentStep === 2 && (
                <ProductPropertiesStep
                  productItems={productItems}
                  products={productsData}
                  selectedSupplierId={selectedSupplierId}
                  onUpdateItem={handleUpdateItem}
                  onUpdateProperties={handleUpdateProperties}
                  initialProductUnits={productUnits}
                />
              )}

              {currentStep === 3 && (
                <ConfirmationStep
                  productItems={productItems}
                  selectedBranch={selectedLocationId}
                  products={productsData}
                  locations={locationsData}
                  productUnits={productUnits}
                />
              )}
            </StepContent>

            {/* Navigation Footer */}
            <NavigationFooter
              currentStep={currentStep}
              isNextDisabled={
                (currentStep === 1 && !validateStep1()) || (currentStep === 2 && !validateStep2())
              }
              onPrevious={handlePreviousStep}
              onNext={handleNextStep}
              onSaveDraft={handleOpenSaveDraftConfirm}
              editMode={editMode}
              recordToEdit={recordToEdit}
              isBusy={isBusy}
            />
          </>
        )}
      </PanelContainer>

      {/* Confirmation Modal (step 3 - complete registration) */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        status={null}
        message={confirmMessage}
        onCancel={resetConfirmation}
        onConfirm={executeConfirmation}
        onReset={resetConfirmation}
        loading={isConfirming}
      />

      {/* Save draft confirmation modal */}
      <GlobalConfirmationModal
        open={showSaveDraftConfirmModal}
        onClose={handleCancelSaveDraftConfirm}
        onConfirm={handleConfirmSaveDraft}
        title="Guardar borrador"
        message="¿Desea guardar el borrador?"
        confirmText="Sí"
        cancelText="No"
        icon={<CheckCircleIcon />}
        variant="default"
        loading={isSavingDraft}
      />

      <GlobalConfirmationModal
        open={showReductionWarningModal}
        onClose={handleCancelReductionWarning}
        onConfirm={handleConfirmReductionWarning}
        title="Cambio en productos o cantidades"
        message="Al reducir productos o cantidades se perderán las propiedades guardadas de las piezas afectadas. ¿Continuar?"
        confirmText="Continuar"
        cancelText="Cancelar"
        icon={<CheckCircleIcon />}
        variant="default"
        disabled={isBusy}
      />

      {/* Error Snackbar for draft validation/API errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Cancel Confirmation Modal (when clicking X or ESC) */}
      <CloseConfirmationModal
        open={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        onConfirm={cancelOperation}
      />
    </>
  );
};

export default AddInventoryPanel;
